import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock } from "lucide-react";
import { UnlockStatus } from "@/components/UnlockStatus";
import { useUnlock } from "@/context/TokenDataContext";
import { AWAITING_LAUNCH } from "@/config/tokenData";
import { formatCompactUsd, formatProgress } from "@/lib/format";
import { LegendaryMilestone } from "@/components/roadmap/LegendaryMilestone";
import { LegendaryFeed } from "@/components/roadmap/LegendaryEffects";

const STATUS = {
  reached: { label: "REACHED", icon: Check },
  active: { label: "IN PROGRESS", icon: null },
  locked: { label: "LOCKED", icon: Lock },
};

const ProgressBar = ({ progress, testId }) => (
  <div className="rm-progress__bar" data-testid={`${testId}-bar`}>
    <motion.span
      initial={{ width: 0 }}
      animate={{ width: `${progress * 100}%` }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      data-progress={progress}
    />
  </div>
);

const MilestoneProgress = ({ id, cap }) => {
  const { progressTo, isPreLaunch } = useUnlock();
  const progress = progressTo(cap);
  const testId = `roadmap-milestone-${id}-progress`;
  return (
    <div className="rm-progress" data-testid={testId}>
      <div className="rm-progress__meta">
        <span data-testid={`${testId}-label`}>{isPreLaunch ? AWAITING_LAUNCH : "PROGRESS"}</span>
        <span data-testid={`${testId}-percent`}>{formatProgress(progress)}</span>
      </div>
      <ProgressBar progress={progress} testId={testId} />
    </div>
  );
};

const MajorProgress = ({ cap }) => {
  const { marketCap, progressTo, isPreLaunch } = useUnlock();
  const progress = progressTo(cap);
  return (
    <div className="rm-progress rm-progress--major" data-testid="roadmap-10m-progress">
      <div className="rm-progress__meta">
        <span data-testid="roadmap-10m-progress-label">CURRENT PROGRESS</span>
        <span data-testid="roadmap-10m-progress-percent">{formatProgress(progress)}</span>
      </div>
      <ProgressBar progress={progress} testId="roadmap-10m-progress" />
      <div className="rm-progress__meta">
        <span className="!text-white/80" data-testid="roadmap-10m-progress-marketcap">
          {formatCompactUsd(marketCap)} / {formatCompactUsd(cap)}
        </span>
        {isPreLaunch && <span data-testid="roadmap-10m-progress-status">{AWAITING_LAUNCH}</span>}
      </div>
    </div>
  );
};

export const RoadmapMilestone = ({ milestone, state, index, ready }) => {
  const { id, cap, label, title, color, icon: Icon, items, major, products } = milestone;
  const status = STATUS[state] ?? STATUS.locked;
  const reached = state === "reached" || state === "unlocked";
  const prevReached = useRef(null);
  const flashed = useRef(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (!ready) return;
    const was = prevReached.current;
    prevReached.current = reached;
    if (was === false && reached && !flashed.current) {
      flashed.current = true;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 2600 + index * 120);
      return () => clearTimeout(t);
    }
  }, [reached, ready, index]);

  return (
    <motion.div
      className={`rm-item ${major ? "rm-item--major" : ""} ${flash ? "rm-item--flash" : ""}`}
      style={{ "--rm-color": color, "--flash-delay": `${index * 0.12}s` }}
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: Math.min(index * 0.06, 0.5), ease: [0.22, 1, 0.36, 1] }}
      data-testid={`roadmap-milestone-${id}`}
      data-state={state}
      data-flash={flash ? "true" : "false"}
    >
      {major && <LegendaryFeed />}
      <div className={`rm-dot ${major ? "rm-dot--major" : ""} ${state === "active" || (major && state === "locked") ? "rm-dot--pulse" : ""}`}>
        {flash && (
          <>
            <span className="rm-dot__flash" />
            <span className="rm-dot__flash rm-dot__flash--2" />
          </>
        )}
      </div>
      {major ? (
          <LegendaryMilestone cap={cap} title={title} products={products} state={state}>
            <UnlockStatus variant="full" className="mt-1 justify-center" testId="roadmap-10m-unlock-status" />
            <MajorProgress cap={cap} />
          </LegendaryMilestone>
        ) : (
          <div className={`rm-card  rm-card--${state}`}>
            <div>
              <div className="rm-cap">{label ?? formatCompactUsd(cap)}</div>
              <div className="rm-title">{title}</div>
            </div>
            <Icon className="rm-icon" strokeWidth={1.6} />
            <ul className="rm-list">
              {items.map((i) => (
                <li key={i}><Check /> <span>{i}</span></li>
              ))}
            </ul>
            <MilestoneProgress id={id} cap={cap} />
            <div className={`rm-status rm-status--${state}`} data-testid={`roadmap-milestone-${id}-status`}>
              {state === "active" && <span className="h-2 w-2 animate-pulse rounded-full bg-[#7fc0ff] shadow-[0_0_8px_#7fc0ff]" />}
              {status.icon && <status.icon />}
              <span>
                {status.label}
                {state === "locked" && <span className="block text-[0.5rem] font-medium tracking-wider text-white/45">UNLOCKS AT {formatCompactUsd(cap)}</span>}
              </span>
            </div>
          </div>
        )}
    </motion.div>
  );
};
